import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Chart from 'react-apexcharts';
import emailjs from '@emailjs/browser';

// Integrated EmailJS
import { 
  Globe, Zap, MessageSquare, Send, CheckCircle, Calendar, Clock, 
  BarChart, ArrowLeft, ArrowRight, Users, Activity, DollarSign, 
  Share2, MousePointer2, LayoutDashboard, TrendingUp, 
  Search, Bell, Filter, Download, Home, Building2, MapPin, Laptop, Rocket, Cpu, Star
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
};

// --- ENHANCED REAL ESTATE MOCK DATABASE ---
const PROPERTY_DB = {
  buy: [
    { id: 1, type: "Villa", name: "Royal Solomon Villa", price: "$2,500,000", area: "4,500 sqft", info: "5 BHK • Private Pool • Palm Jumeirah", img: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=800" },
    { id: 2, type: "Penthouse", name: "The Skyloft Elite", price: "$4,200,000", area: "6,200 sqft", info: "Panoramic View • 3 Terrace levels • Burj District", img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800" },
    { id: 3, type: "Apartment", name: "Downtown Luxe Suite", price: "$850,000", area: "1,800 sqft", info: "Luxury 2 BHK • Near Business Bay", img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&q=80&w=800" }
  ],
  rent: [
    { id: 4, type: "Apartment", name: "Marina View Studio", price: "$2,000/mo", area: "850 sqft", info: "Fully furnished • Gym access • Dubai Marina", img: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&q=80&w=800" },
    { id: 5, type: "Office", name: "Solomon HQ Suite", price: "$15,000/mo", area: "3,000 sqft", info: "Commercial Grade • 10 Parking Slots", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=800" },
    { id: 6, type: "Villa", name: "Suburban Retreat", price: "$5,000/mo", area: "3,200 sqft", info: "Family friendly • Gated community • JVC", img: "https://a0.muscache.com/im/pictures/miso/Hosting-1060560399778339576/original/95a96a8c-ffd6-414f-98b1-a4579d9b9204.jpeg" }
  ]
};

// --- PROJECT 01: SOLOMON CONSTRUCTIONS REAL ESTATE ---
const WebsiteDemo = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'bot', content: "Welcome to Solomon Constructions. Are you looking to buy or rent luxury property today?" }]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef(null);

  const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => { scrollToBottom() }, [messages]);

  const handleChat = (e) => {
    e.preventDefault();
    if (!input) return;
    const userMsg = input.toLowerCase();
    let botReply = "I'm not sure about that specific detail, but I can connect you with a Solomon agent. Head down to the booking section to schedule a visit!";
    if (userMsg.includes("buy") || userMsg.includes("purchase") || userMsg.includes("sale")) {
      botReply = `For purchase, we have: 1. ${PROPERTY_DB.buy[0].name} (${PROPERTY_DB.buy[0].price}), 2. ${PROPERTY_DB.buy[1].name} (${PROPERTY_DB.buy[1].price}), 3. ${PROPERTY_DB.buy[2].name} (${PROPERTY_DB.buy[2].price}). Which one would you like details on?`;
    } 
    else if (userMsg.includes("rent") || userMsg.includes("lease")) {
      botReply = `Current rentals: ${PROPERTY_DB.rent[0].name} at ${PROPERTY_DB.rent[0].price}, ${PROPERTY_DB.rent[1].name} at ${PROPERTY_DB.rent[1].price}, and ${PROPERTY_DB.rent[2].name} at ${PROPERTY_DB.rent[2].price}.`;
    }
    else if (userMsg.includes("price") || userMsg.includes("how much")) {
        if(userMsg.includes("villa")) botReply = `The Royal Solomon Villa is priced at ${PROPERTY_DB.buy[0].price}.`;
        else if(userMsg.includes("skyloft")) botReply = `The Skyloft Elite is ${PROPERTY_DB.buy[1].name}.`;
        else botReply = "Our prices range from $2,000/mo for rentals to $4.2M for luxury penthouses. Any specific property in mind?";
    }
    else if (userMsg.includes("sq foot") || userMsg.includes("sqft") || userMsg.includes("area") || userMsg.includes("size")) {
        botReply = `Our largest unit is the ${PROPERTY_DB.buy[1].name} at ${PROPERTY_DB.buy[1].area}. The Royal Villa is ${PROPERTY_DB.buy[0].area}. Which size are you looking for?`;
    }
    else if (userMsg.includes("appointment") || userMsg.includes("book") || userMsg.includes("schedule") || userMsg.includes("visit")) {
      botReply = "Excellent choice. Please use the 'Confirm Visit Schedule' form at the bottom of the page to lock in your slot!";
    }

    setMessages([...messages, { role: 'user', content: input }, { role: 'bot', content: botReply }]);
    setInput("");
  };

  return (
    <div className="min-h-screen bg-[#0c0a09] text-white font-sans selection:bg-orange-500/30">
      <nav className="p-6 flex justify-between items-center border-b border-orange-500/10 bg-black/40 backdrop-blur-md sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2 text-orange-500 font-black uppercase text-xs tracking-tighter"><ArrowLeft size={16}/> SOLOMON STRIVE</Link>
        <div className="text-xl font-black italic tracking-tighter uppercase">SOLOMON <span className="text-orange-500">CONSTRUCTIONS</span></div>
        <a href="#booking-section" className="px-5 py-2 bg-orange-600 hover:bg-orange-500 rounded-full text-[10px] font-black uppercase tracking-widest transition-all">Book Appointment</a>
      </nav>

      <header className="py-24 px-10 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-orange-600/5 blur-[120px] rounded-full" />
        <h1 className="text-6xl md:text-8xl font-black uppercase italic tracking-tighter mb-4 relative z-10">Find Your <span className="text-orange-500">Legacy</span> Home</h1>
        <p className="text-gray-400 max-w-xl mx-auto relative z-10">Luxury real estate integrated with Solomon AI for seamless viewing and acquisition in Dubai Real Estate Market.</p>
      </header>

      <main className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <h2 className="col-span-full text-xs font-black text-orange-500 uppercase tracking-[0.4em] mb-4">Available for Purchase</h2>
          {PROPERTY_DB.buy.map(item => (
            <div key={item.id} className="group relative rounded-[32px] overflow-hidden border border-white/5 bg-white/5 backdrop-blur-xl transition-all hover:border-orange-500/50 shadow-2xl">
              <img src={item.img} className="w-full h-64 object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" alt={item.name} />
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] font-black bg-orange-500/20 text-orange-400 px-2 py-1 rounded uppercase">{item.type}</span>
                  <span className="text-xl font-black text-orange-500">{item.price}</span>
                </div>
                <h3 className="text-xl font-bold mb-1">{item.name}</h3>
                <p className="text-gray-400 text-xs mb-1 font-mono uppercase">{item.area}</p>
                <p className="text-gray-500 text-sm mb-6">{item.info}</p>
                <button onClick={() => setChatOpen(true)} className="w-full py-3 bg-white/5 border border-white/10 hover:bg-orange-600 hover:border-orange-600 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all">Enquire via AI</button>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-32">
          <h2 className="col-span-full text-xs font-black text-orange-400 uppercase tracking-[0.4em] mb-4">Available for Rent</h2>
          {PROPERTY_DB.rent.map(item => (
            <div key={item.id} className="group relative rounded-[32px] overflow-hidden border border-white/5 bg-white/5 backdrop-blur-xl transition-all hover:border-orange-400/50 shadow-2xl">
              <img src={item.img} className="w-full h-64 object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" alt={item.name} />
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] font-black bg-orange-400/10 text-orange-300 px-2 py-1 rounded uppercase">{item.type}</span>
                  <span className="text-xl font-black text-orange-400">{item.price}</span>
                </div>
                <h3 className="text-xl font-bold mb-1">{item.name}</h3>
                <p className="text-gray-400 text-xs mb-1 font-mono uppercase">{item.area}</p>
                <p className="text-gray-500 text-sm mb-6">{item.info}</p>
                <button onClick={() => setChatOpen(true)} className="w-full py-3 bg-white/5 border border-white/10 hover:bg-orange-500/20 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all">Check Availability</button>
              </div>
            </div>
          ))}
        </div>

        <section id="booking-section" className="scroll-mt-24 max-w-3xl mx-auto bg-white/5 border border-orange-500/10 p-12 rounded-[40px] shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-600/10 blur-3xl rounded-full" />
            <h2 className="text-3xl font-black uppercase italic tracking-tighter mb-2 text-center relative z-10">Book an <span className="text-orange-500">Appointment</span></h2>
            <p className="text-gray-400 text-center mb-8 text-sm relative z-10">Fill your details below to schedule a visit to the property.</p>
            <form className="space-y-4 relative z-10">
                <div className="grid md:grid-cols-2 gap-4">
                    <input type="text" placeholder="Your Full Name" className="w-full bg-black/40 border border-white/10 p-4 rounded-2xl outline-none focus:border-orange-500 text-sm transition-all" />
                    <input type="email" placeholder="Email Address" className="w-full bg-black/40 border border-white/10 p-4 rounded-2xl outline-none focus:border-orange-500 text-sm transition-all" />
                </div>
                <input type="text" placeholder="Property Name (e.g. Royal Solomon Villa)" className="w-full bg-black/40 border border-white/10 p-4 rounded-2xl outline-none focus:border-orange-500 text-sm transition-all" />
                <div className="grid md:grid-cols-2 gap-4">
                    <input type="date" className="w-full bg-black/40 border border-white/10 p-4 rounded-2xl outline-none focus:border-orange-500 text-sm text-gray-400" />
                    <input type="time" className="w-full bg-black/40 border border-white/10 p-4 rounded-2xl outline-none focus:border-orange-500 text-sm text-gray-400" />
                </div>
                <button type="button" className="w-full py-5 bg-orange-600 hover:bg-orange-500 text-white font-black rounded-2xl tracking-[0.2em] uppercase transition-all shadow-lg shadow-orange-600/20">Confirm Visit Schedule</button>
            </form>
        </section>
      </main>

      <div className="fixed bottom-6 right-6 z-50">
        {!chatOpen && (
          <motion.button 
                whileHover={{ scale: 1.1 }} 
                whileTap={{ scale: 0.9 }}
                onClick={() => setChatOpen(true)}
                className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center shadow-2xl shadow-orange-600/40 border border-orange-400/20"
            >
              <MessageSquare className="text-white" size={28} />
            </motion.button>
        )}
        
        <AnimatePresence>
            {chatOpen && (
            <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 50 }} 
                animate={{ opacity: 1, scale: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.9, y: 50 }} 
                className="w-96 h-[550px] bg-[#1c1917] border border-orange-500/20 rounded-[30px] shadow-2xl flex flex-col overflow-hidden"
            >
                <div className="p-6 bg-orange-600 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-orange-600 font-bold">S</div>
                        <div className="flex flex-col">
                            <span className="font-black text-[10px] uppercase tracking-widest text-white">Solomon AI Concierge</span>
                            <span className="text-[8px] text-orange-100 uppercase tracking-tighter">Online • Ready to Assist</span>
                        </div>
                    </div>
                    <button onClick={() => setChatOpen(false)} className="text-white/70 hover:text-white transition-colors">✕</button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]">
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] p-4 rounded-2xl text-xs leading-relaxed ${m.role === 'user' ? 'bg-orange-600 text-white shadow-lg' : 'bg-white/5 text-gray-200 border border-white/10 backdrop-blur-md'}`}>
                                {m.content}
                            </div>
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>

                <form onSubmit={handleChat} className="p-4 bg-black/40 border-t border-white/5 flex gap-2">
                    <input 
                        value={input} 
                        onChange={(e) => setInput(e.target.value)} 
                        placeholder="Ask about price, sqft, buy/rent..." 
                        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs outline-none focus:border-orange-500 text-white transition-all" 
                    />
                    <button type="submit" className="p-3 bg-orange-600 hover:bg-orange-500 rounded-xl transition-all"><Send size={16}/></button>
                </form>
            </motion.div>
            )}
        </AnimatePresence>
      </div>

      <footer className="py-10 text-center border-t border-white/5 opacity-50 text-[10px] tracking-widest font-black uppercase">
          © 2026 Solomon Constructions Dubai • Luxury AI Integrated Real Estate
      </footer>
    </div>
  );
};

// --- CRM & DASHBOARD ---
const CRMDemo = () => {
    const [activeTab, setActiveTab] = useState('Dashboard');
    const [leads, setLeads] = useState(1204);
    
    useEffect(() => {
      const interval = setInterval(() => {
        setLeads(prev => prev + Math.floor(Math.random() * 2));
      }, 4000);
      return () => clearInterval(interval);
    }, []);

    const revenueChartOptions = {
      chart: { id: 'revenue-growth', toolbar: { show: false }, background: 'transparent' },
      colors: ['#10b981'],
      stroke: { curve: 'smooth', width: 3 },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0 } },
      xaxis: { categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], labels: { style: { colors: '#10b981' } } },
      yaxis: { show: false },
      grid: { borderColor: '#064e3b', strokeDashArray: 4 },
      theme: { mode: 'dark' },
      tooltip: { theme: 'dark' }
    };
    const revenueSeries = [{ name: 'Revenue', data: [31, 40, 28, 51, 42, 109, 100] }];

    const donutOptions = {
      labels: ['Converted', 'In Progress', 'Cold'],
      colors: ['#10b981', '#065f46', '#022c22'],
      plotOptions: { pie: { donut: { size: '70%', labels: { show: true, name: { color: '#fff' }, value: { color: '#10b981' } } } } },
      stroke: { show: false },
      legend: { show: false }
    };

    return (
      <div className="min-h-screen bg-[#010a05] text-white flex font-sans selection:bg-emerald-500/30">
        <aside className="w-20 lg:w-64 border-r border-emerald-900/30 h-screen p-6 hidden md:flex flex-col sticky top-0">
          <div className="text-xl font-black italic mb-10 text-emerald-500 tracking-tighter">SOLOMON<span className="text-white">CRM</span></div>
          <nav className="space-y-2 flex-1">
            {[
              { n: 'Dashboard', i: <LayoutDashboard size={20}/> },
              { n: 'Leads', i: <Users size={20}/> },
              { n: 'Analytics', i: <TrendingUp size={20}/> }
            ].map(item => (
              <div 
                key={item.n} 
                onClick={() => setActiveTab(item.n)}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${activeTab === item.n ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-gray-500 hover:text-emerald-400'}`}>
                {item.i} <span className="hidden lg:block text-xs font-black uppercase tracking-widest">{item.n}</span>
              </div>
            ))}
          </nav>
        </aside>

        <main className="flex-1 p-4 md:p-10">
          <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
            <div>
              <Link to="/" className="inline-flex items-center gap-2 text-emerald-500 mb-4 font-black uppercase text-[10px] tracking-widest hover:text-white transition-all">
                <ArrowLeft size={14} /> Back to Solomon Strive
              </Link>
              <h1 className="text-4xl font-black italic tracking-tighter uppercase text-emerald-50">{activeTab} <span className="text-emerald-500">Panel</span></h1>
            </div>
            
            <div className="flex items-center gap-4 bg-emerald-950/20 p-2 rounded-2xl border border-emerald-500/10 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500/50" />
                  <input className="bg-transparent border-none outline-none pl-10 pr-4 py-2 text-sm text-white w-full" placeholder="Search data..." />
                </div>
                <div className="flex gap-2 pl-4 border-l border-emerald-500/10">
                  <div className="p-2 text-emerald-500 hover:bg-emerald-500/10 rounded-lg cursor-pointer"><Bell size={20}/></div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-black font-black">JS</div>
                </div>
            </div>
          </header>
  
          {activeTab === 'Dashboard' && (
            <motion.div initial={{opacity:0}} animate={{opacity:1}}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[
                  {l: "Active Leads", v: leads.toLocaleString(), i: <Users/>, d: "+12.5%"},
                  {l: "Revenue", v: "$84,200", i: <DollarSign/>, d: "+8.2%"},
                  {l: "Conversion", v: "94.2%", i: <Zap/>, d: "+2.1%"},
                  {l: "Bot Activity", v: "Live", i: <Activity/>, d: "Stable"}
                ].map((s, idx) => (
                  <div key={idx} className="p-6 bg-emerald-950/20 border border-emerald-500/10 rounded-[35px]">
                    <div className="flex justify-between items-start mb-4">
                      <div className="p-3 bg-emerald-500/5 text-emerald-500 rounded-xl">{s.i}</div>
                      <span className="text-[10px] font-black text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-md">{s.d}</span>
                    </div>
                    <div className="text-[10px] text-emerald-500/60 font-black uppercase tracking-[0.2em]">{s.l}</div>
                    <div className="text-3xl font-black mt-1 tabular-nums">{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="bg-emerald-950/10 border border-emerald-500/10 p-10 rounded-[40px]">
                  <h3 className="text-sm font-black uppercase tracking-widest mb-8 text-emerald-500 text-center italic">Welcome to Solomon Intelligence v2.0</h3>
                  <p className="text-emerald-50/50 text-center">Select "Leads" or "Analytics" from the sidebar to view detailed business records.</p>
              </div>
            </motion.div>
          )}
  
          {activeTab === 'Leads' && (
            <motion.div initial={{opacity:0, x:20}} animate={{opacity:1, x:0}} className="bg-emerald-950/10 border border-emerald-500/10 rounded-[40px] overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-emerald-500/5 text-emerald-500 text-[10px] font-black uppercase tracking-widest">
                  <tr>
                    <th className="p-6">Contact Name</th>
                    <th className="p-6">Status</th>
                    <th className="p-6">Source</th>
                    <th className="p-6">Value</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-emerald-50/80">
                  {[
                    {n: "Alex Rivera", s: "High Interest", src: "AI Chatbot", v: "$2,400"},
                    {n: "Sarah Chen", s: "Closed", src: "Direct", v: "$12,000"},
                    {n: "Marcus Wright", s: "In Progress", src: "Website", v: "$500"},
                    {n: "Elena Gomez", s: "Cold", src: "AI Chatbot", v: "$0"}
                  ].map((lead, i) => (
                    <tr key={i} className="border-b border-emerald-500/5">
                      <td className="p-6 font-bold">{lead.n}</td>
                      <td className="p-6"><span className="px-3 py-1 bg-emerald-500/10 rounded-full text-[10px]">{lead.s}</span></td>
                      <td className="p-6 italic">{lead.src}</td>
                      <td className="p-6 font-black text-emerald-50">{lead.v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          )}
  
          {activeTab === 'Analytics' && (
            <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}}>
              <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 bg-emerald-950/10 border border-emerald-500/10 p-8 rounded-[40px]">
                    <div className="flex justify-between items-center mb-8">
                      <h3 className="font-black uppercase tracking-widest text-sm text-emerald-500">Revenue Stream (7 Days)</h3>
                    </div>
                    <Chart options={revenueChartOptions} series={revenueSeries} type="area" height={280} />
                  </div>
                  <div className="bg-emerald-950/10 border border-emerald-500/10 p-8 rounded-[40px] flex flex-col justify-center items-center">
                    <h3 className="font-black uppercase tracking-widest text-sm text-emerald-500 mb-6">Conversion Funnel</h3>
                    <Chart options={donutOptions} series={[65, 25, 10]} type="donut" width="280" />
                  </div>
              </div>
            </motion.div>
          )}
        </main>
      </div>
    );
};

// --- MAIN HOMEPAGE (SOLOMON STRIVE) ---
const HomePage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '', date: '', time: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  const words = ["Websites", "Automations", "Revenue"];
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);

  useEffect(() => {
    if (subIndex === words[index].length + 1 && !reverse) {
      setTimeout(() => setReverse(true), 2500);
      return;
    }
    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % words.length);
      return;
    }
    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? 40 : 100);
    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse]);

  // FIXED: EmailJS integration with client email capture and auto-hide success message
  const handleBooking = (e) => {
    e.preventDefault();
    setIsSending(true);

    const serviceId = 'service_solomonstrivein';
    const templateId = 'template_2wsnzxr';
    const publicKey = 'ZoVYLdOpVhu0kVaSS';

    const templateParams = {
        from_name: formData.name,
        reply_to: formData.email, // Captures client email correctly for outreach
        date: formData.date,
        time: formData.time,
        message: formData.message,
    };

    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setShowSuccess(true);
        setFormData({ name: '', email: '', message: '', date: '', time: '' });
        
        // Success message disappears after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert("Submission failed. Falling back to email client...");
        const subject = encodeURIComponent(`Audit Request: ${formData.name}`);
        const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nDate: ${formData.date}\nTime: ${formData.time}\n\nMessage:\n${formData.message}`);
        window.location.href = `mailto:solomonstrivein@gmail.com?subject=${subject}&body=${body}`;
      })
      .finally(() => setIsSending(false));
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white selection:bg-blue-500/30 overflow-x-hidden font-sans scroll-smooth">
      <nav className="fixed w-full z-50 border-b border-white/5 bg-[#020617]/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xl font-black tracking-tighter italic cursor-default">
            SOLOMON<span className="text-blue-500 underline decoration-blue-500/30 underline-offset-4 ml-1">STRIVE</span>
          </motion.div>
          <div className="hidden md:flex gap-10 items-center">
            {['Services', 'Work', 'Pricing'].map((item) => (
              <motion.a key={item} href={`#${item.toLowerCase()}`} whileHover={{ y: -2 }} className="text-xs font-bold text-gray-400 hover:text-white transition-colors uppercase tracking-[0.2em]">{item}</motion.a>
            ))}
            <motion.a href="#testimonials" whileHover={{ y: -2 }} className="text-xs font-bold text-gray-400 hover:text-white transition-colors uppercase tracking-[0.2em]">Testimonials</motion.a>
            <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="#book-audit" className="px-8 py-2.5 bg-blue-600 text-white text-[10px] font-black rounded-full tracking-widest uppercase shadow-lg shadow-blue-500/40">Book an Audit</motion.a>
          </div>
        </div>
      </nav>

      <section className="relative pt-64 pb-32 px-6 flex flex-col items-center text-center overflow-hidden">
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-8xl font-black mb-8 leading-[1.1] tracking-tighter max-w-6xl">
          Scale Your Business with <br /> 
          <div className="min-h-[1.2em] mt-2">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500 italic">
              AI Integrated {words[index].substring(0, subIndex)}
              <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.8, repeat: Infinity }} className="text-white ml-1 inline-block">|</motion.span>
            </span>
          </div>
        </motion.h1>
        <p className="text-gray-400 text-xl max-w-2xl mb-12 font-light italic">Tailored digital ecosystems for high-growth agencies.</p>
        <motion.a whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} href="#book-audit" className="px-14 py-6 bg-blue-600 text-white font-black rounded-2xl transition-all shadow-[0_20px_50px_rgba(37,99,235,0.3)] uppercase tracking-widest">BOOK AN AUDIT FOR FREE</motion.a>
      </section>

      <section id="services" className="py-32 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-xs font-bold text-blue-500 tracking-[0.4em] uppercase mb-16">High-Performance Services</h2>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { icon: <Laptop className="text-blue-500" />, title: "Web Architecture", desc: "Customized, conversion focused websites tailored for your needs and scaling your business." },
              { icon: <Cpu className="text-blue-500" />, title: "AI Agents", desc: "Custom AI agents that handle your sales & customer support 24/7." },
              { icon: <Rocket className="text-blue-500" />, title: "CRM Automation", desc: "Automate your lead tracking and sales pipeline instantly without missing a lead." }
            ].map((s, i) => (
              <motion.div key={i} {...fadeInUp} className="p-12 rounded-[50px] bg-white/5 border border-white/5 hover:border-blue-500/20 transition-all text-left">
                <div className="mb-6">{s.icon}</div>
                <h3 className="text-2xl font-black uppercase tracking-tighter mb-4 italic">{s.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="work" className="py-20 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xs font-bold text-blue-500 tracking-[0.4em] uppercase mb-12">Live Demo Projects (Click to view)</h2>
          <div className="grid md:grid-cols-2 gap-10">
            <Link to="/demo-website" className="group relative h-[450px] rounded-[40px] overflow-hidden border border-white/10 shadow-2xl bg-slate-900 transition-all hover:border-orange-500/50">
              <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=1200" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-[1500ms]" alt="Project 1" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
              <div className="absolute bottom-10 left-10 text-left">
                <span className="text-orange-500 text-xs font-bold tracking-widest uppercase font-mono">Project 01</span>
                <h3 className="text-3xl font-black mt-3 uppercase tracking-tighter text-white">Real Estate Website + AI Chatbot Integration</h3>
                <div className="text-orange-500 font-black text-xs mt-3 flex items-center gap-2 group-hover:translate-x-2 transition-transform uppercase">EXPLORE Website + AI Agent Project <ArrowRight size={14}/></div>
              </div>
            </Link>

            <Link to="/demo-crm" className="group relative h-[450px] rounded-[40px] overflow-hidden border border-white/10 shadow-2xl bg-slate-900 transition-all hover:border-emerald-500/50">
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-[1500ms]" alt="Project 2" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
              <div className="absolute bottom-10 left-10 text-left">
                <span className="text-emerald-500 text-xs font-bold tracking-widest uppercase font-mono">Project 02</span>
                <h3 className="text-3xl font-black mt-3 uppercase tracking-tighter text-white">AI CRM Dashboard & Operations Tracking</h3>
                <div className="text-emerald-500 font-black text-xs mt-3 flex items-center gap-2 group-hover:translate-x-2 transition-transform uppercase">EXPLORE AI Business Workflow Project <ArrowRight size={14}/></div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-32 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-xs font-bold text-blue-500 tracking-[0.4em] uppercase mb-16">Pricing Structures</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <div className="p-10 rounded-[40px] bg-white/5 border border-white/5 text-left relative overflow-hidden group hover:border-blue-500/30 transition-all">
                    <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform"><Zap size={80}/></div>
                    <h3 className="text-2xl font-black italic uppercase mb-2">The Scale Pack</h3>
                    <p className="text-gray-400 text-sm mb-8">Perfect for emerging agencies.</p>
                    <div className="text-5xl font-black mb-8">$299<span className="text-lg text-gray-500">/project</span></div>
                    <ul className="space-y-4 mb-10">
                        {['Custom Website', 'Basic AI Agent Integrated in Website', 'Basic CRM Setup', '1 week After Launch Support'].map(li => (
                            <li key={li} className="flex items-center gap-3 text-sm text-gray-300"><CheckCircle size={16} className="text-blue-500"/> {li}</li>
                        ))}
                    </ul>
                    <a href="#book-audit" className="block w-full py-4 bg-white/10 hover:bg-white/20 text-center rounded-2xl font-black text-xs uppercase tracking-widest transition-all">Choose Scale</a>
                </div>
                <div className="p-10 rounded-[40px] bg-blue-600 text-left relative overflow-hidden shadow-2xl shadow-blue-600/20">
                    <div className="absolute top-0 right-0 p-8 opacity-20"><Rocket size={80}/></div>
                    <h3 className="text-2xl font-black italic uppercase mb-2">The Legacy Pack</h3>
                    <p className="text-blue-100 text-sm mb-8">Complete ecosystem dominance.</p>
                    <div className="text-5xl font-black mb-8">$599<span className="text-lg text-blue-200">/project</span></div>
                    <ul className="space-y-4 mb-10">
                        {['Advanced AI integrated Website', 'Entire Business Workflow Automation', 'Premium CRM Integration', 'Priority 24/7 Support For 3 weeks After Launch'].map(li => (
                            <li key={li} className="flex items-center gap-3 text-sm text-white"><CheckCircle size={16} className="text-white"/> {li}</li>
                        ))}
                    </ul>
                    <a href="#book-audit" className="block w-full py-4 bg-white text-blue-600 text-center rounded-2xl font-black text-xs uppercase tracking-widest transition-all">Go Legacy</a>
                </div>
            </div>
        </div>
      </section>

      <section id="testimonials" className="py-32 px-6 scroll-mt-20 bg-black/20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xs font-bold text-blue-500 tracking-[0.4em] uppercase mb-16 text-center">Ecosystem Feedback</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Luke", role: "CEO, BMB Crypto", text: "Solomon Strive didn't just build a site, they built a revenue machine. Our lead conversion jumped 40% in 30 days." },
              { name: "Gaurav", role: "Founder, Galaxy Institute", text: "The AI integration is seamless. It handles 90% of my initial inquiries of my coaching perfectly. Best investment this year." },
              { name: "Mark", role: "Owner & Chef, Omnos Pizzeria", text: "A team that understands resturants needs. The CRM automation saved us 20 hours a week of billing and operation management issues." }
            ].map((t, i) => (
              <motion.div key={i} {...fadeInUp} className="p-8 rounded-[30px] bg-white/5 border border-white/5 flex flex-col justify-between">
                <div>
                  <div className="flex gap-1 mb-6 text-blue-500"><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={0} fill="currentColor"/></div>
                  <p className="text-gray-300 italic mb-8 italic">"{t.text}"</p>
                </div>
                <div>
                  <p className="font-black uppercase tracking-tighter text-white">{t.name}</p>
                  <p className="text-blue-500 text-[10px] font-bold uppercase tracking-widest">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    
      {/* --- PAYMENTS ACCEPTED SECTION --- */}
<div className="max-w-7xl mx-auto px-6 mb-12">
  <div className="flex flex-col items-center justify-center">
    <p className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500/60 mb-6">Secured & Accepted Payments</p>
    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-50 hover:opacity-100 transition-opacity duration-500">
      
      {/* PayPal */}
      <div className="flex items-center gap-2 group">
        <div className="w-8 h-8 bg-blue-600/10 rounded-lg flex items-center justify-center group-hover:bg-blue-600/20 transition-colors">
          <svg className="w-5 h-5 fill-blue-500" viewBox="0 0 24 24"><path d="M7.076 21.337H9l.864-5.464h2.417c3.155 0 5.584-1.28 6.257-4.805.35-1.83.102-3.32-.934-4.385-1.008-1.04-2.73-1.614-5.06-1.614H6.314a.65.65 0 0 0-.642.548L3.012 20.79a.65.65 0 0 0 .641.747h3.423z"/></svg>
        </div>
        <span className="text-xs font-black italic tracking-tighter uppercase">PayPal</span>
      </div>

      {/* Wise */}
      <div className="flex items-center gap-2 group">
        <div className="w-8 h-8 bg-emerald-600/10 rounded-lg flex items-center justify-center group-hover:bg-emerald-600/20 transition-colors">
          <svg className="w-5 h-5 fill-emerald-500" viewBox="0 0 24 24"><path d="M0 0h11.134l2.126 5.86h-7.01l4.814 4.814-1.558 1.558L4.01 6.736l-.377 2.384-2.022-.32L3.13 0zM24 0H12.866l-2.126 5.86h7.01l-4.814 4.814 1.558 1.558 5.496-5.496.377-2.384 2.022.32L20.87 0z"/></svg>
        </div>
        <span className="text-xs font-black italic tracking-tighter uppercase">Wise</span>
      </div>

      {/* UPI */}
      <div className="flex items-center gap-2 group">
        <div className="w-8 h-8 bg-orange-600/10 rounded-lg flex items-center justify-center group-hover:bg-orange-600/20 transition-colors">
           <Zap className="w-4 h-4 text-orange-500" />
        </div>
        <div className="flex flex-col">
            <span className="text-xs font-black italic tracking-tighter uppercase">UPI</span>
            <span className="text-[7px] font-bold text-gray-500 uppercase tracking-widest leading-none">Instant Transfer</span>
        </div>
      </div>

    </div>
  </div>
</div>
{/* --- END PAYMENTS ACCEPTED --- */}
  
      <section id="book-audit" className="py-32 px-6 scroll-mt-20 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-3/4 bg-blue-600/5 blur-[120px] rounded-full -z-10" />
        <div className="max-w-3xl mx-auto bg-white/5 border border-white/10 p-12 rounded-[50px] shadow-2xl backdrop-blur-xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black uppercase italic tracking-tighter mb-4 italic">Request a <span className="text-blue-500 underline underline-offset-8">Strategy Audit</span></h2>
            <p className="text-gray-400 text-sm">Let’s analyze your current infrastructure and find your growth bottlenecks.</p>
          </div>

          <form onSubmit={handleBooking} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-4">Full Name</label>
                <input required type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Your Name" className="w-full bg-black/40 border border-white/10 p-5 rounded-2xl outline-none focus:border-blue-500 text-sm transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-4">Email Address</label>
                <input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="youremailid@gmail.com" className="w-full bg-black/40 border border-white/10 p-5 rounded-2xl outline-none focus:border-blue-500 text-sm transition-all" />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-4">Preferred Date</label>
                <input required type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full bg-black/40 border border-white/10 p-5 rounded-2xl outline-none focus:border-blue-500 text-sm transition-all text-gray-400" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-4">Time Slot</label>
                <input required type="time" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="w-full bg-black/40 border border-white/10 p-5 rounded-2xl outline-none focus:border-blue-500 text-sm transition-all text-gray-400" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-4">Message / Business Goals</label>
              <textarea rows="4" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Tell us about your current needs of service and goals..." className="w-full bg-black/40 border border-white/10 p-5 rounded-2xl outline-none focus:border-blue-500 text-sm transition-all resize-none"></textarea>
            </div>

            <button type="submit" disabled={isSending} className="w-full py-6 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl tracking-[0.2em] uppercase transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50">
              {isSending ? "Processing..." : "Initialize Audit Request"}
            </button>
            
            {showSuccess && <p className="text-emerald-500 text-[10px] font-black uppercase text-center tracking-widest animate-pulse">Request Sent Successfully!</p>}
          </form>
        </div>
      </section>

      <footer className="py-20 text-center border-t border-white/5 bg-black/40">
        <div className="text-xl font-black tracking-tighter italic mb-8">SOLOMON<span className="text-blue-500">STRIVE</span></div>
        <p className="text-[10px] tracking-widest font-black uppercase opacity-40">© 2026 Solomon Strive • High Growth Digital Ecosystems</p>
      </footer>
    </div>
  );
};

// --- ROUTING SETUP ---
const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/demo-website" element={<WebsiteDemo />} />
        <Route path="/demo-crm" element={<CRMDemo />} />
      </Routes>
    </Router>
  );
};

export default App;